﻿import React, {ChangeEvent, HTMLInputAutoCompleteAttribute, JSX, useCallback, useEffect, useState} from "react";
import {useControlId} from "../helpers/useControlId";
import Input, {HtmlInputType} from "../HtmlControls/Input";
import {MvvmContextState} from "./MvvmContextState";
import {MvvmPrefixedControlProps} from "./MvvmControlProps";

import {useRegisterValidation} from "./useRegisterValidation";

export type MvvmInputProps<T extends object, TValue> = {
	control: MvvmPrefixedControlProps<T, TValue>;
	type: HtmlInputType;
	valueToString: (value: TValue | undefined) => string;
	valueFromString: (value: string | undefined) => TValue;
	updateOnChange: boolean;
	max?: number;
	min?: number;
	step?: number;
	maxLength?: number;
	minLength?: number;
	autoComplete?: HTMLInputAutoCompleteAttribute;
	autoCapitalize?: "off" | "none" | "on" | "sentences" | "words" | "characters" | undefined | (string & {});
	enterKeyHint?: "enter" | "done" | "go" | "next" | "previous" | "search" | "send" | undefined;
	inputMode?: "none" | "text" | "tel" | "url" | "email" | "numeric" | "decimal" | "search" | undefined;
};

export default function MvvmInput<T extends object = never, TValue = never>(props: MvvmInputProps<T, TValue>): JSX.Element {
	const {
		control,
		type,
		valueToString,
		valueFromString,
		updateOnChange,
		max,
		min,
		step,
		maxLength,
		minLength,
		autoComplete,
		autoCapitalize,
		enterKeyHint,
		inputMode
	} = props;

	const {
		context,
		property,
		id,
		label,
		hint,
		placeholder,
		column,
		controlPrefix,
		controlSuffix,
		hidden,
		disabled,
		validation,
		onChange
	} = control;

	const controlId = useControlId(id);
	const readValue = context.getValue<TValue>(property);
	const [value, setValue] = useState(() => valueToString(readValue));

	const onChangeInternal = useCallback(function (e: ChangeEvent<HTMLInputElement>): void {
		setValue(e.target.value);
		const value: TValue = valueFromString(e.target.value);
		(context as MvvmContextState<T>).setValue(property, value, updateOnChange);
		if (onChange) onChange(value);
	}, [context, property, validation, valueFromString, controlId, onChange]);

	const onBlurInternal = useCallback(function (): void {
		(context as MvvmContextState<T>).enqueueValidation(property);
	}, [context, property, validation, controlId]);

	useEffect(() => {
		const stringValue = valueToString(readValue);
		if (stringValue !== value) {
			setValue(stringValue);
		}
	}, [context, property, valueToString, readValue]);

	useRegisterValidation(context, property, controlId, validation);

	return <Input label={label}
	              name={property}
	              type={type}
	              value={value}
	              controlSuffix={controlSuffix}
	              controlPrefix={controlPrefix}
	              hint={hint}
	              hidden={hidden}
	              column={column}
	              id={id}
	              required={validation?.required != null}
	              error={context.getError(property)?.error}
	              disabled={disabled}
	              placeholder={placeholder}
	              max={max}
	              min={min}
	              step={step}
	              maxLength={maxLength}
	              minLength={minLength}
	              autoFocus={control.autoFocus}
	              autoCapitalize={autoCapitalize}
	              autoComplete={autoComplete}
	              enterKeyHint={enterKeyHint}
	              inputMode={inputMode}
	              onChange={onChangeInternal}
	              onBlur={onBlurInternal}
	/>;
};
